import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { AlertTriangle, FileWarning, Receipt, Loader2 } from 'lucide-react';

const PURPOSES = [
  { value: 'activation', label: 'Activation (₹999)' },
  { value: 'final',      label: 'Final / Balance' },
  { value: 'full',       label: 'Full payment' },
  { value: 'addon',      label: 'Add-on / Customization' },
];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/**
 * Recover Missing Invoices — SMB Lead Detail card.
 *
 * Lists credit transactions matched to this lead's business_name that
 * DON'T have an `invoice_id`. For each, sales can:
 *   1. Confirm gross amount (defaults to txn.amount — editable so
 *      the operator can enter ₹999 gross for a ₹975 net Razorpay row)
 *   2. Optionally supply the Razorpay `pay_xxx` id — auto-fetches +
 *      books the PG-fee expense in the same shot
 *   3. Pick purpose (activation / final / full / addon)
 * → Generates the tax invoice + stamps it on the EXISTING txn row (no
 * duplicate txn).
 *
 * Card is auto-hidden when no orphans are found.
 */
export const BackfillInvoicePanel = ({ lead, onRecovered }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addons, setAddons] = useState([]);
  const [dialogTxn, setDialogTxn] = useState(null);
  const [form, setForm] = useState({
    purpose: 'activation', gross_amount: '', razorpay_payment_id: '',
    smb_addon_id: '', notes: '',
  });
  const [busy, setBusy] = useState(false);

  const fetchOrphans = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/smb/leads/${lead.id}/orphan-txns`),
        api.get(`/smb/leads/${lead.id}/addons`).catch(() => ({ data: { data: [] } })),
      ]);
      setRows(r1.data?.data || []);
      setAddons((r2.data?.data || []).filter((a) => ['draft', 'link_sent'].includes(a.status)));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => { fetchOrphans(); }, [fetchOrphans]);

  const openDialog = (txn) => {
    setDialogTxn(txn);
    // Detect a Razorpay reference so we can auto-fill and unlock PG-fee fetch.
    const looksLikeRzp = /^pay_[A-Za-z0-9]+$/.test((txn.reference || '').trim());
    setForm({
      purpose: 'activation',
      gross_amount: String(txn.amount ?? ''),
      razorpay_payment_id: looksLikeRzp ? txn.reference.trim() : '',
      smb_addon_id: '',
      notes: '',
    });
  };

  const submit = async () => {
    const gross = parseFloat(form.gross_amount);
    if (!gross || gross <= 0) { toast.error('Enter a valid gross amount'); return; }
    if (form.purpose === 'addon' && !form.smb_addon_id) {
      toast.error('Pick an add-on when purpose is Add-on'); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/smb/leads/${lead.id}/backfill-invoice`, {
        existing_txn_id: dialogTxn.id,
        purpose: form.purpose,
        gross_amount: gross,
        razorpay_payment_id: form.razorpay_payment_id.trim(),
        smb_addon_id: form.smb_addon_id || undefined,
        notes: form.notes.trim(),
      });
      if (r.data?.success) {
        const feeLabel = r.data.fee_result?.amount
          ? ` · Fee ${fmtINR(r.data.fee_result.amount)}`
          : '';
        toast.success(`Invoice ${r.data.invoice_number} generated${feeLabel}`);
        setDialogTxn(null);
        fetchOrphans();
        if (onRecovered) onRecovered(r.data);
      } else {
        toast.error(r.data?.error || 'Backfill failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Don't render the card at all when there's nothing to recover.
  if (loading || rows.length === 0) return null;

  return (
    <>
      <Card className="mb-4 border-amber-500/40" data-testid="backfill-invoice-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-amber-500">
            <FileWarning className="h-4 w-4" />
            Recover Missing Invoices
            <span className="text-xs text-muted-foreground font-normal">
              · {rows.length} credit transaction{rows.length > 1 ? 's' : ''} for this lead lack a tax invoice
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="divide-y divide-border/50">
            {rows.map((r) => (
              <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`orphan-txn-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-emerald-400">{fmtINR(r.amount)}</span>
                    <Badge variant="outline" className="text-[10px]">{r.method || '—'}</Badge>
                    <span className="text-xs text-muted-foreground">{fmtDate(r.date)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {r.description || '—'}
                    {r.reference && <span className="ml-2 font-mono">· {r.reference}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => openDialog(r)}
                  data-testid={`backfill-btn-${r.id}`}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Generate Invoice
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={!!dialogTxn} onOpenChange={(v) => { if (!v && !busy) setDialogTxn(null); }}>
        <DialogContent className="max-w-md" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-amber-500" />
              Recover Invoice for Transaction
            </DialogTitle>
            <DialogDescription>
              Attach a fresh tax invoice to this existing credit. We&apos;ll update the
              txn amount to the gross value + optionally book the Razorpay fee.
              No duplicate transaction is created.
            </DialogDescription>
          </DialogHeader>

          {dialogTxn && (
            <div className="text-xs bg-muted/30 rounded p-2 space-y-0.5">
              <div><span className="text-muted-foreground">Existing txn:</span> {fmtINR(dialogTxn.amount)} on {fmtDate(dialogTxn.date)}</div>
              <div className="truncate"><span className="text-muted-foreground">Description:</span> {dialogTxn.description}</div>
              {dialogTxn.reference && <div className="font-mono"><span className="text-muted-foreground">Ref:</span> {dialogTxn.reference}</div>}
            </div>
          )}

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Purpose *</Label>
                <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })} disabled={busy}>
                  <SelectTrigger data-testid="bf-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Gross amount (₹) *</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.gross_amount}
                  onChange={(e) => setForm({ ...form, gross_amount: e.target.value })}
                  disabled={busy}
                  data-testid="bf-gross-amount"
                />
                <div className="text-[10px] text-muted-foreground">
                  Enter the invoiced amount (pre-PG-fee). Difference = Razorpay fee.
                </div>
              </div>
            </div>

            {form.purpose === 'addon' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Add-on *</Label>
                <Select value={form.smb_addon_id} onValueChange={(v) => setForm({ ...form, smb_addon_id: v })} disabled={busy}>
                  <SelectTrigger data-testid="bf-addon">
                    <SelectValue placeholder={addons.length ? 'Pick add-on…' : 'No draft/pending add-ons'} />
                  </SelectTrigger>
                  <SelectContent>
                    {addons.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.description.slice(0, 40)} — {fmtINR(a.amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Razorpay payment_id (optional)</Label>
              <Input
                value={form.razorpay_payment_id}
                onChange={(e) => setForm({ ...form, razorpay_payment_id: e.target.value })}
                disabled={busy}
                placeholder="pay_XXXXXXXXXXXX"
                className="font-mono text-xs"
                data-testid="bf-rzp-id"
              />
              <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Providing this auto-fetches the exact PG fee from Razorpay + books the expense.
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                disabled={busy}
                rows={2}
                placeholder="Context for the audit trail"
                data-testid="bf-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogTxn(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="bf-submit"
            >
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating…</> : 'Generate Invoice & Book Fee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BackfillInvoicePanel;
